/**
 * MessengerContextMenu — независимый React-меню для сообщения на /messenger.
 * Полностью отдельный от vanilla chatVanillaMenu (тот используется на /chat).
 *
 * - Light theme
 * - Позиционирование 5/30/50: 5px от клика; флип при 30px до края; 50px от края после флипа
 * - Emoji-pill сверху + action items + reactions section снизу
 * - Twemoji для emoji
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import twemoji from "twemoji";
import {
  REACTION_EMOJI, userAvatarUrl,
  type ChatMessage,
} from "@/api/dashboard";
import { Icon } from "@/components/prototype/Icon";
import { Emoji } from "@/components/Emoji";

const QUICK_REACTIONS = REACTION_EMOJI.slice(0, 6);

const PALETTE = ["#4fae4e","#3390ec","#9c7ce5","#00acc1","#e85c5c","#e99a12"];
function colorFor(uid: string): string {
  const hash = (uid || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length];
}

export function MessengerContextMenu({
  message, x, y, myUserId, isSuper,
  onClose,
  onReact,
  onMoreReactions,
  onReply,
  onQuoteReply,
  onCopyText,
  onEdit,
  onPin,
  onForward,
  onSaveToDrive,
  onDelete,
  onRestore,
  onSelect,
  onJumpToProfile,
  onShowReadBy,
  quotedText,
  attachmentId,
  onDownloadAttachment,
}: {
  message: ChatMessage;
  x: number; y: number;
  myUserId: string | null;
  isSuper: boolean;
  /** Захваченный ChatPage'ом текст выделения В МОМЕНТ открытия меню.
   *  Если задан — показываем «Цитировать выделенное». */
  quotedText?: string;
  onClose: () => void;
  onReact: (emoji: string) => void;
  onMoreReactions: (anchor: HTMLElement) => void;
  onReply: () => void;
  /** Ответить с цитатой выделенного фрагмента. Если undefined — пункт не показывается. */
  onQuoteReply?: (quote: string) => void;
  onCopyText: () => void;
  onEdit: () => void;
  onPin: () => void;
  onForward: () => void;
  onSaveToDrive?: (items: Array<{ attachmentId: number; filename?: string }>) => void;
  /** Конкретное вложение, по которому открыли меню (long-press / правый клик
   *  по видео или фото). Если не задано и вложение одно — берём его. */
  attachmentId?: number;
  /** Скачать вложение на устройство (ПК — <a download>, телефон — Web Share). */
  onDownloadAttachment?: (att: { attachmentId: number; filename?: string }) => void;
  onDelete: () => void;
  onRestore: () => void;
  onSelect: () => void;
  onJumpToProfile: (userId: string) => void;
  onShowReadBy?: (users: NonNullable<ChatMessage["read_by"]>) => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // visible:false до тех пор пока useLayoutEffect не вычислит позицию —
  // это убирает «дёргание» (рендер на 0,0 → snap на нужную позицию).
  const [pos, setPos] = useState<{ left: number; top: number; visible: boolean }>({
    left: x + 5, top: y + 5, visible: false,
  });
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);

  const mine = message.author_user_id === myUserId;
  const isDeleted = !!message.deleted_at;
  // Вложение для пункта «Скачать»: то, по которому кликнули, иначе единственное.
  const atts = message.attachments ?? [];
  const dlAtt = (attachmentId != null ? atts.find(a => a.id === attachmentId) : undefined)
    ?? (atts.length === 1 ? atts[0] : undefined);
  const dlMime = (dlAtt?.mime || "").toLowerCase();
  const dlIsVideo = dlMime.startsWith("video/") || /\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(dlAtt?.filename || "");
  const dlIsImage = !dlIsVideo && (dlMime.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic)$/i.test(dlAtt?.filename || ""));
  const dlLabel = dlIsVideo ? "Скачать видео" : dlIsImage ? "Сохранить фото" : "Скачать файл";
  const canEdit = mine && !message.deleted_at && message.id > 0;
  const canDelete = (mine || isSuper) && !message.deleted_at && message.id > 0;
  // Текст цитирования приходит пропом от ChatPage (захвачен в openContextMenu
  // синхронно, ДО setState/re-render → до того как selection убивается).
  // Читать window.getSelection() уже поздно, mount происходит после React
  // commit'а и на mobile iOS уже прибрал selection UI.
  const quoteSnapshot = quotedText || "";
  const canRestore = (mine || isSuper) && isDeleted && message.id > 0;

  // Twemoji parse
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    try { twemoji.parse(el, { folder: "svg", ext: ".svg", base: "/twemoji/" }); } catch { /* ignore */ }
  }, [message.id]);

  // useLayoutEffect — синхронно ДО paint. Меню появляется уже на корректной
  // позиции, без визуального «дёргания».
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 5;
    const NEAR_EDGE = 30;
    const FLIP_MARGIN = 50;
    let nx = x + GAP;
    let ny = y + GAP;
    if (nx + rect.width > vw - NEAR_EDGE) {
      nx = x - rect.width - GAP;
      if (nx < FLIP_MARGIN) nx = FLIP_MARGIN;
    }
    if (ny + rect.height > vh - NEAR_EDGE) {
      ny = y - rect.height - GAP;
      if (ny < FLIP_MARGIN) ny = FLIP_MARGIN;
    }
    if (nx < FLIP_MARGIN) nx = FLIP_MARGIN;
    if (ny < FLIP_MARGIN) ny = FLIP_MARGIN;
    setPos({ left: nx, top: ny, visible: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.id, x, y]);

  // Закрытие по клику вне / Esc
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (wrapRef.current && !wrapRef.current.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    // setTimeout чтобы текущий contextmenu клик не закрыл сразу
    const t = window.setTimeout(() => {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const reactions = (message.reactions ?? []) as Array<{
    emoji: string; count: number; mine: boolean;
    users?: Array<{ user_id: string; display_name: string; avatar_filename: string | null }>;
  }>;

  return (
    <div
      ref={wrapRef}
      className="msgr-ctx-menu"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 1500,
        visibility: pos.visible ? "visible" : "hidden",
      }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Quick emoji pill — не показываем для удалённого сообщения */}
      {!isDeleted && (
      <div className="msgr-ctx-pill">
        {QUICK_REACTIONS.map((emo) => {
          const active = reactions.find(r => r.emoji === emo && r.mine);
          return (
            <button
              key={emo}
              type="button"
              className={`msgr-ctx-pill-btn${active ? " is-active" : ""}`}
              onClick={() => { onReact(emo); onClose(); }}
            ><Emoji native={emo} eager/></button>
          );
        })}
        <button
          ref={moreBtnRef}
          type="button"
          className="msgr-ctx-pill-more"
          onClick={() => moreBtnRef.current && onMoreReactions(moreBtnRef.current)}
          title="Больше реакций"
        >
          <Icon name="chevron_d" size={14}/>
        </button>
      </div>
      )}

      {/* Action items */}
      <div className="msgr-ctx-list">
        {isDeleted ? (
          /* Удалённое сообщение — только Восстановить + Выбрать */
          <>
            {canRestore && (
              <button className="msgr-ctx-item" onClick={() => { onRestore(); onClose(); }}>
                <Icon name="refresh" size={18}/><span>Восстановить</span>
              </button>
            )}
            <button className="msgr-ctx-item" onClick={() => { onSelect(); onClose(); }}>
              <Icon name="check" size={18}/><span>Выбрать</span>
            </button>
          </>
        ) : (
          <>
            <button className="msgr-ctx-item" onClick={() => { onReply(); onClose(); }}>
              <Icon name="reply" size={18}/><span>Ответить</span>
            </button>
            {quoteSnapshot && onQuoteReply && (
              <button
                className="msgr-ctx-item"
                onClick={() => { onQuoteReply(quoteSnapshot); onClose(); }}
              >
                <Icon name="reply" size={18}/><span>Цитировать</span>
              </button>
            )}
            <button className="msgr-ctx-item" onClick={() => { onCopyText(); onClose(); }}>
              <Icon name="copy" size={18}/><span>Скопировать</span>
            </button>
            {canEdit && (
              <button className="msgr-ctx-item" onClick={() => { onEdit(); onClose(); }}>
                <Icon name="edit" size={18}/><span>Изменить</span>
              </button>
            )}
            <button className="msgr-ctx-item" onClick={() => { onPin(); onClose(); }}>
              <Icon name="pin" size={18}/><span>{message.pinned_at ? "Открепить" : "Закрепить"}</span>
            </button>
            <button className="msgr-ctx-item" onClick={() => { onForward(); onClose(); }}>
              <Icon name="external" size={18}/><span>Переслать</span>
            </button>
            {onDownloadAttachment && dlAtt && (
              <button
                className="msgr-ctx-item"
                onClick={() => {
                  onDownloadAttachment({ attachmentId: dlAtt.id, filename: dlAtt.filename });
                  onClose();
                }}
              >
                <Icon name="download" size={18}/><span>{dlLabel}</span>
              </button>
            )}
            {onSaveToDrive && message.attachments && message.attachments.length > 0 && (
              <button
                className="msgr-ctx-item"
                onClick={() => {
                  // Все аттачи сообщения — сохраняем разом в одну папку
                  const items = message.attachments!.map(a => ({ attachmentId: a.id, filename: a.filename }));
                  onSaveToDrive(items);
                  onClose();
                }}
              >
                <Icon name="folder" size={18}/>
                <span>Добавить на диск{message.attachments.length > 1 ? ` (${message.attachments.length})` : ""}</span>
              </button>
            )}
            {canDelete && (
              <button className="msgr-ctx-item is-danger" onClick={() => { onDelete(); onClose(); }}>
                <Icon name="trash" size={18}/><span>Удалить</span>
              </button>
            )}
            <button className="msgr-ctx-item" onClick={() => { onSelect(); onClose(); }}>
              <Icon name="check" size={18}/><span>Выбрать</span>
            </button>
          </>
        )}
      </div>

      {/* Reactions list (если есть) */}
      {reactions.length > 0 && (
        <>
          <div className="msgr-ctx-divider"/>
          <div className="msgr-ctx-reactions">
            {reactions.map((r) => {
              const soloUser = r.count === 1 && (r.users?.length ?? 0) === 1 ? r.users![0] : null;
              return (
              <div key={r.emoji} className="msgr-ctx-react-row">
                <span className="msgr-ctx-react-emoji"><Emoji native={r.emoji} eager/></span>
                <span className="msgr-ctx-react-num">{r.count}</span>
                {soloUser ? (
                  <button
                    type="button"
                    className="msgr-ctx-react-solo"
                    onClick={() => { onJumpToProfile(soloUser.user_id); onClose(); }}
                  >
                    <span className="msgr-ctx-react-ava">
                      {(() => {
                        const url = userAvatarUrl(soloUser.user_id, soloUser.avatar_filename);
                        const initial = (soloUser.display_name || "?").trim().charAt(0).toUpperCase();
                        return url ? <img src={url} alt=""/> : <span style={{ background: colorFor(soloUser.user_id) }}>{initial}</span>;
                      })()}
                    </span>
                    <span className="msgr-ctx-react-name">{soloUser.display_name}</span>
                  </button>
                ) : (
                  <div className="msgr-ctx-react-avatars">
                    {(r.users || []).slice(0, 5).map((u) => {
                      const url = userAvatarUrl(u.user_id, u.avatar_filename);
                      const initial = (u.display_name || "?").trim().charAt(0).toUpperCase();
                      return (
                        <button
                          key={u.user_id}
                          type="button"
                          className="msgr-ctx-react-ava"
                          onClick={() => { onJumpToProfile(u.user_id); onClose(); }}
                          title={u.display_name}
                        >
                          {url ? (
                            <img src={url} alt=""/>
                          ) : (
                            <span style={{ background: colorFor(u.user_id) }}>{initial}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </>
      )}

      {/* Прочитали (N) — кликабельная строка с eye на ЛЮБОМ не-удалённом
          сообщении (раньше было только своё). Видно кто из участников чата
          прочитал. */}
      {!message.deleted_at && (message.read_by?.length ?? 0) > 0 && (
        <>
          <div className="msgr-ctx-divider"/>
          <button
            type="button"
            className="msgr-ctx-readby-row"
            onClick={() => {
              if (onShowReadBy) {
                onShowReadBy(message.read_by!);
                onClose();
              }
            }}
          >
            <span className="msgr-ctx-readby-icon" aria-hidden="true">
              <Icon name="eye" size={18}/>
            </span>
            <span className="msgr-ctx-readby-count">{message.read_by!.length}</span>
            {message.read_by!.length === 1 ? (() => {
              const u = message.read_by![0];
              const url = userAvatarUrl(u.user_id, u.avatar_filename ?? null);
              const initial = (u.display_name || "?").trim().charAt(0).toUpperCase();
              return (
                <button
                  type="button"
                  className="msgr-ctx-readby-solo"
                  onClick={() => { onJumpToProfile(u.user_id); onClose(); }}
                >
                  <span className="msgr-ctx-readby-ava">
                    {url ? <img src={url} alt=""/> : <span style={{ background: colorFor(u.user_id) }}>{initial}</span>}
                  </span>
                  <span className="msgr-ctx-readby-name">{u.display_name}</span>
                </button>
              );
            })() : (
              <div className="msgr-ctx-readby-avas">
                {message.read_by!.slice(0, 5).map((u) => {
                  const url = userAvatarUrl(u.user_id, u.avatar_filename ?? null);
                  const initial = (u.display_name || "?").trim().charAt(0).toUpperCase();
                  return (
                    <span key={u.user_id} className="msgr-ctx-readby-ava" title={u.display_name}>
                      {url ? (
                        <img src={url} alt=""/>
                      ) : (
                        <span style={{ background: colorFor(u.user_id) }}>{initial}</span>
                      )}
                    </span>
                  );
                })}
                {message.read_by!.length > 5 && (
                  <span className="msgr-ctx-readby-ava msgr-ctx-readby-more">
                    +{message.read_by!.length - 5}
                  </span>
                )}
              </div>
            )}
          </button>
        </>
      )}
    </div>
  );
}
